import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import TextareaAutosize from "react-textarea-autosize";
import { graphqlClient } from "@/lib/graphql";
import { useAuth } from "@/hooks/useAuth";
import type { Message } from "@/types";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";

const MESSAGE_THREAD_QUERY = gql`
  query MessageThread($threadId: String!) {
    messageThread(threadId: $threadId) {
      id
      body
      read
      createdAt
      sender {
        id
        displayName
        avatarUrl
      }
      recipient {
        id
        displayName
        avatarUrl
      }
      listing {
        id
        price
        displayTitle
        displayImageUrl
        game {
          id
          title
          thumbnailUrl
        }
      }
    }
  }
`;

const USER_QUERY = gql`
  query User($id: ID!) {
    user(id: $id) {
      id
      displayName
      avatarUrl
    }
  }
`;

const LISTING_QUERY = gql`
  query Listing($id: ID!) {
    listing(id: $id) {
      id
      price
      displayTitle
      displayImageUrl
      game {
        id
        title
        thumbnailUrl
      }
    }
  }
`;

const SEND_MESSAGE_MUTATION = gql`
  mutation SendMessage($recipientId: ID!, $body: String!, $listingId: ID) {
    sendMessage(recipientId: $recipientId, body: $body, listingId: $listingId) {
      message {
        id
        body
        createdAt
        sender {
          id
          displayName
          avatarUrl
        }
        recipient {
          id
          displayName
          avatarUrl
        }
      }
      errors
    }
  }
`;

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString("lt-LT", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function MessageThreadPage() {
  const { threadId } = useParams<{ threadId: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const sellerId = searchParams.get("seller");
  const listingId = searchParams.get("listing");

  const { data, isLoading } = useQuery({
    queryKey: ["messageThread", threadId],
    queryFn: () =>
      graphqlClient.request<{ messageThread: Message[] }>(
        MESSAGE_THREAD_QUERY,
        { threadId }
      ),
    enabled: !!threadId,
    refetchInterval: 10000,
  });

  const messages = data?.messageThread || [];

  // Fetch seller info when starting a new conversation with no messages
  const { data: sellerData } = useQuery({
    queryKey: ["user", sellerId],
    queryFn: () =>
      graphqlClient.request<{ user: { id: string; displayName: string; avatarUrl: string | null } }>(
        USER_QUERY,
        { id: sellerId }
      ),
    enabled: !!sellerId && messages.length === 0,
  });

  // Fetch listing info when starting a new conversation with no messages
  const { data: listingData } = useQuery({
    queryKey: ["listing", listingId],
    queryFn: () =>
      graphqlClient.request<{ listing: { id: string; price: number | null; displayTitle: string; displayImageUrl: string | null; game: { id: string; title: string; thumbnailUrl: string | null } | null } | null }>(
        LISTING_QUERY,
        { id: listingId }
      ),
    enabled: !!listingId && messages.length === 0,
  });

  const otherUser = messages.length > 0
    ? messages[0].sender.id === user?.id
      ? messages[0].recipient
      : messages[0].sender
    : sellerData?.user || null;

  const listing = messages.find((m) => m.listing)?.listing || listingData?.listing || null;

  const sendMutation = useMutation({
    mutationFn: (vars: { recipientId: string; body: string; listingId?: string }) =>
      graphqlClient.request<{
        sendMessage: { message: Message | null; errors: string[] };
      }>(SEND_MESSAGE_MUTATION, vars),
    onSuccess: (data) => {
      if (data.sendMessage.message) {
        setBody("");
        queryClient.invalidateQueries({ queryKey: ["messageThread", threadId] });
        queryClient.invalidateQueries({ queryKey: ["messageThreads"] });
        queryClient.invalidateQueries({ queryKey: ["unreadCounts"] });
      }
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Hide footer and lock viewport so chat fills the screen
  useEffect(() => {
    const layoutRoot = document.getElementById("root")?.firstElementChild as HTMLElement | null;
    const footer = layoutRoot?.querySelector("footer") as HTMLElement | null;
    if (layoutRoot) {
      layoutRoot.style.height = "100dvh";
      layoutRoot.style.overflow = "hidden";
    }
    if (footer) footer.style.display = "none";
    return () => {
      if (layoutRoot) {
        layoutRoot.style.height = "";
        layoutRoot.style.overflow = "";
      }
      if (footer) footer.style.display = "";
    };
  }, []);

  const submitMessage = () => {
    if (!body.trim() || !otherUser) return;

    sendMutation.mutate({
      recipientId: otherUser.id,
      body: body.trim(),
      listingId: listing?.id || listingId || undefined,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitMessage();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      submitMessage();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col overflow-hidden px-4 sm:px-6 lg:px-8" style={{ height: "calc(100dvh - 4rem)" }}>
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-200 py-4">
        <Link
          to="/messages"
          className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>

        {otherUser && (
          <Link to={`/users/${otherUser.id}`} className="flex items-center gap-3">
            {otherUser.avatarUrl ? (
              <img
                src={otherUser.avatarUrl}
                alt={otherUser.displayName}
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-800 text-sm font-semibold text-white">
                {otherUser.displayName[0].toUpperCase()}
              </div>
            )}
            <span className="font-semibold text-gray-900">
              {otherUser.displayName}
            </span>
          </Link>
        )}
      </div>

      {/* Listing reference */}
      {listing && (
        <Link
          to={`/listings/${listing.id}`}
          className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3"
        >
          {listing.displayImageUrl && (
            <img
              src={listing.displayImageUrl}
              alt={listing.displayTitle}
              className="h-10 w-10 rounded object-cover"
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">
              {listing.displayTitle}
            </p>
            {listing.price != null && (
              <p className="text-sm font-bold text-primary-800">
                {listing.price.toFixed(2)} &euro;
              </p>
            )}
          </div>
          <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4">
        {messages.length === 0 ? (
          <p className="text-center text-sm text-gray-500">
            Pradėkite pokalbį parašydami žinutę.
          </p>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => {
              const isOwn = msg.sender.id === user?.id;
              return (
                <div
                  key={msg.id}
                  className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                      isOwn
                        ? "rounded-br-md bg-primary-800 text-white"
                        : "rounded-bl-md bg-gray-100 text-gray-900"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                    <p
                      className={`mt-1 text-[11px] ${
                        isOwn ? "text-primary-200" : "text-gray-400"
                      }`}
                    >
                      {formatTime(msg.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Reply input */}
      <form
        onSubmit={handleSubmit}
        className="flex items-end gap-3 border-t border-gray-200 py-4"
      >
        <TextareaAutosize
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Rašykite žinutę..."
          className="input flex-1 resize-none"
          disabled={sendMutation.isPending}
          minRows={1}
          maxRows={6}
        />
        <button
          type="submit"
          disabled={!body.trim() || sendMutation.isPending}
          className="btn-primary shrink-0"
        >
          {sendMutation.isPending ? (
            <LoadingSpinner className="h-4 w-4" />
          ) : (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </button>
      </form>
    </div>
  );
}
